/**
 * Analytics Event Type Definitions
 *
 * This module defines all analytics events tracked in the app.
 * Using a discriminated union pattern ensures type safety:
 * - Event name must match its params type
 * - TypeScript prevents invalid event/param combinations
 */

import { AppScreens } from '@/shared/types';

export enum AnalyticsEventName {
  // Section entry/exit events
  SectionEntry = 'section_entry',
  SectionExit = 'section_exit',

  // App lifecycle events
  AppBackground = 'app_background',

  // Screen view event (one event, varied params per screen type)
  ScreenView = 'screen_view',

  // Completion events
  InteractiveChapterCompleted = 'interactive_chapter_completed',
  VideoChapterCompleted = 'video_chapter_completed',
  PrayerTalkCompleted = 'prayer_talk_completed',

  // Share events
  SharedClickInteractiveBibleSlide = 'shared_click_interactive_bible_slide',
  SharedClickVideoBibleSlide = 'shared_click_video_bible_slide',
  SharedClickPrayerTalkSlide = 'shared_click_prayer_talk_slide',

  // Auth events
  AuthClick = 'auth_click',
  AuthSuccess = 'auth_success',
  AuthError = 'auth_error',
  AuthCancelled = 'auth_cancelled',

  // Engagement events
  FirstMeaningfulEngagement = 'first_meaningful_engagement',

  // Reading Plan events
  RPStarted = 'reading_plan_started',
  RPRestarted = 'reading_plan_restarted',
  RPDayCompleted = 'reading_plan_day_completed',
  RPCompleted = 'reading_plan_completed',

  // Onboarding events
  OnboardingView = 'onboarding_view',
  OnboardingSlideView = 'onboarding_slide_view',
  OnboardingNextClick = 'onboarding_next_click',
  OnboardingBackClick = 'onboarding_back_click',
  OnboardingSessionEnd = 'onboarding_session_end',

  // App session events
  AppSessionStart = 'app_session_start',
}

// ============================================================================
// Event Parameter Types (per event)
// ============================================================================

export interface AppBackgroundParams extends Record<string, unknown> {
  appSection?: string;
  appRoute?: string;
}

/**
 * Screen view params - supports all screen types
 * screenName identifies which screen
 * Additional params vary per screen type
 */
export interface ScreenViewParams extends Record<string, unknown> {
  screenName: string;
  // Content-specific params (all optional, vary per screen)
  book?: string;
  chapter?: string;
  talkId?: string;
  talkTitle?: string;
  talkDate?: string;
  openingDate?: string;
  openingTime?: string;
  roadmapId?: string;
  roadmapName?: string;
}

export interface InteractiveChapterCompletedParams extends Record<string, unknown> {
  book?: string;
  chapter?: string;
  roadmapId?: string;
  roadmapName?: string;
}

export interface VideoChapterCompletedParams extends Record<string, unknown> {
  book?: string;
  chapter?: string;
}

export interface PrayerTalkCompletedParams extends Record<string, unknown> {
  talkId?: string;
  talkTitle?: string;
  talkDate?: string;
  openingDate?: string;
  openingTime?: string;
}

export interface SharedClickInteractiveBibleSlideParams extends Record<string, unknown> {
  origin?: string;
  appSection?: string;
  slideId?: string;
  shareId?: string;
}

export interface SharedClickVideoBibleSlideParams extends Record<string, unknown> {
  origin?: string;
  appSection?: string;
  reference?: string;
  shareId?: string;
}

export interface SharedClickPrayerTalkSlideParams extends Record<string, unknown> {
  origin?: string;
  appSection?: string;
  reference?: string;
  shareId?: string;
  prayerTalkTitle?: string;
}

export interface AuthClickParams extends Record<string, unknown> {
  provider: 'google' | 'apple';
}

export interface AuthSuccessParams extends Record<string, unknown> {
  provider: 'google' | 'apple';
  isRegistration: boolean;
}

export interface AuthErrorParams extends Record<string, unknown> {
  provider: 'google' | 'apple';
  errorType: 'provider_error' | 'api_error' | 'unknown';
}

export interface AuthCancelledParams extends Record<string, unknown> {
  provider: 'google' | 'apple';
}

export interface FirstMeaningfulEngagementParams extends Record<string, unknown> {
  sectionName: 'video_bible' | 'prayer_talk' | 'reading_plan' | 'story' | 'interactive_bible';
  engagementTimeMs: number;
  timeToActivationMs: number;
}

export interface RPStartedParams extends Record<string, unknown> {
  plan_id: string;
  plan_title: string;
}

export interface RPRestartedParams extends Record<string, unknown> {
  plan_id: string;
  plan_title: string;
}

export interface RPDayCompletedParams extends Record<string, unknown> {
  plan_id: string;
  plan_title: string;
  day_number: number;
}

export interface RPCompletedParams extends Record<string, unknown> {
  plan_id: string;
  plan_title: string;
  days_total: number;
}

/**
 * Source names for analytics events
 * @param Home - user opened the app section by tapping the item in Home page.
 * @param Menu - user opened the app section by tapping the menu list.
 * @param Onboarding - user opened the app section by tapping CTA button in the last slide of onboarding.
 * @param DeepLink - user opened the app by tapping a deep link.
 *  - External links only. For example: user shared a story with a friend,
 *  - the friend clicked on the link. Or user received an email from OhMyBible,
 *  - and opened the link in the email.
 * @param Push - any input via push notification. It doesn't matter whether the push leads to a specific slide or is a general reminder.
 * @param Navigation - user opened the app section by tapping on icon in the navigation bar.
 * @param Organic - user opened the app by tapping the icon on the home screen.
 */

export enum SectionEntrySourceNames {
  Home = 'home',
  Menu = 'menu',
  Onboarding = 'onboarding',
  DeepLink = 'deep_link',
  Push = 'push',
  Navigation = 'navigation',
  Organic = 'organic',
}

export interface SectionEntryParams extends Record<string, unknown> {
  section_name: AppScreens; // Section the user entered
  source: SectionEntrySourceNames; // Where the user came from before entering the section
}

export interface SectionExitParams extends Record<string, unknown> {
  section_name: AppScreens; // Section the user left
  section_time_spent_ms: number; // Active foreground time spent in the section (ms)
}

export enum OnboardingSlideId {
  ProblemNoisyWorld = 'problem_statement',
  WhyWeBuiltOhMyBible = 'brand_intro',
  ManyWaysToMeetGod = 'product_overview',
  VideoBibleFeature = 'feature_video_bible',
  InteractiveChaptersFeature = 'feature_stories',
  PrayerExperienceFeature = 'feature_prayer',
  StartJourneyCta = 'call_to_action',
}

export interface OnboardingViewParams extends Record<string, unknown> {
  onboarding_session_id: string;
}

export interface OnboardingSlideViewParams extends OnboardingViewParams, Record<string, unknown> {
  onboarding_slide_id: OnboardingSlideId;
  onboarding_slide_index: number; // Slide number in the Onboarding slide queue
  onboarding_time_on_slide_ms: number; // How many milliseconds the user spent on this screen
  onboarding_action: 'next' | 'back'; // What did the user do to exit the screen?
}

export interface OnboardingNextClickParams extends OnboardingViewParams, Record<string, unknown> {
  onboarding_from_slide_id: OnboardingSlideId;
}

export interface OnboardingBackClickParams extends OnboardingNextClickParams {}

/**
 * Onboarding session end params
 * @param onboarding_session_number: Sequential number of the onboarding entry.
 * - Incremented by 1 when the user returns to onboarding after the app
 * - has been in the background for more than 30 minutes. At the time of the
 * - onboarding_session_end event, it shows which entry this event occurred in.
 * @param onboarding_time_spent_ms: time from the last foregrounding of the app to the next onboarding_session_end event.
 * @param onboarding_total_time_spent_ms: total time spent during the entire last successful session
 *  - (from the first slide to tapping the CTA button on the last slide).
 * @param onboarding_back_actions: contains indices of slides where the user clicked the BACK button. Example: back_actions = [2, 3, 6]
 * @param onboarding_total_back_count: length of the back_actions array.
 * @param onboarding_is_final: indicates whether onboarding was completed successfully or not.
 * - TRUE - if the user clicked the CTA button on the last slide.
 * - FALSE - if the user minimized or closed the app without finishing onboarding.
 */

export interface OnboardingSessionEndParams extends OnboardingViewParams, Record<string, unknown> {
  onboarding_session_number: number;
  onboarding_time_spent_ms: number;
  onboarding_total_time_spent_ms: number;
  onboarding_back_actions: number[];
  onboarding_total_back_count: number;
  onboarding_is_final: boolean;
}

/**
 * App session source names
 * @param Organic - user opened the app by tapping the icon on the home screen.
 * @param Push - any input via push notification. It doesn't matter whether the push leads to a specific slide or is a general reminder.
 * @param DeepLink - user opened the app by tapping a deep link.
 *  - External links only. For example: user shared a story with a friend,
 *  - the friend clicked on the link. Or user received an email from OhMyBible,
 *  - and opened the link in the email.
 */

export enum AppSessionSourceNames {
  Organic = 'organic',
  Push = 'push',
  DeepLink = 'deep_link',
}

/**
 * App session delivery channel names
 * Determined by the utm_source parameter in the URL
 * For session_source: push — delivery_channel is NOT passed.
 * For session_source: organic — delivery_channel is NOT passed.
 */

export enum AppSessionDeliveryChannelNames {
  Instagram = 'instagram',
  Facebook = 'facebook',
  TikTok = 'tiktok',
  Email = 'email',
  YouTube = 'youtube',
  Pinterest = 'pinterest',
  Threads = 'threads',
  Landing = 'landing',
  Share = 'share',
  Unknown = 'unknown',
}

export interface AppSessionParams extends Record<string, unknown> {
  sessionSource: AppSessionSourceNames;
  deliveryChannel?: AppSessionDeliveryChannelNames;
}

// ============================================================================
// Discriminated Union: AnalyticsEvent
// ============================================================================
// This ensures event names and params are type-safe.
// Example: AnalyticsEvent with name AppBackground MUST have AppBackgroundParams

export type AnalyticsEvent =
  | { name: AnalyticsEventName.AppBackground; params: AppBackgroundParams }
  | { name: AnalyticsEventName.ScreenView; params: ScreenViewParams }
  | { name: AnalyticsEventName.InteractiveChapterCompleted; params: InteractiveChapterCompletedParams }
  | { name: AnalyticsEventName.VideoChapterCompleted; params: VideoChapterCompletedParams }
  | { name: AnalyticsEventName.PrayerTalkCompleted; params: PrayerTalkCompletedParams }

  | { name: AnalyticsEventName.SharedClickInteractiveBibleSlide; params: SharedClickInteractiveBibleSlideParams }
  | { name: AnalyticsEventName.SharedClickVideoBibleSlide; params: SharedClickVideoBibleSlideParams }
  | { name: AnalyticsEventName.SharedClickPrayerTalkSlide; params: SharedClickPrayerTalkSlideParams }

  | { name: AnalyticsEventName.AuthClick; params: AuthClickParams }
  | { name: AnalyticsEventName.AuthSuccess; params: AuthSuccessParams }
  | { name: AnalyticsEventName.AuthError; params: AuthErrorParams }
  | { name: AnalyticsEventName.AuthCancelled; params: AuthCancelledParams }

  | { name: AnalyticsEventName.FirstMeaningfulEngagement; params: FirstMeaningfulEngagementParams }

  | { name: AnalyticsEventName.RPStarted; params: RPStartedParams }
  | { name: AnalyticsEventName.RPRestarted; params: RPRestartedParams }
  | { name: AnalyticsEventName.RPDayCompleted; params: RPDayCompletedParams }
  | { name: AnalyticsEventName.RPCompleted; params: RPCompletedParams }

  | { name: AnalyticsEventName.SectionEntry; params: SectionEntryParams }
  | { name: AnalyticsEventName.SectionExit; params: SectionExitParams }

  | { name: AnalyticsEventName.OnboardingView; params: OnboardingViewParams }
  | { name: AnalyticsEventName.OnboardingSlideView; params: OnboardingSlideViewParams }
  | { name: AnalyticsEventName.OnboardingNextClick; params: OnboardingNextClickParams }
  | { name: AnalyticsEventName.OnboardingBackClick; params: OnboardingBackClickParams }
  | { name: AnalyticsEventName.OnboardingSessionEnd; params: OnboardingSessionEndParams }

  | { name: AnalyticsEventName.AppSessionStart; params: AppSessionParams };

